import { read, remove, write } from "../storage"

/**
 * The chat session a browser keeps for a surface, so a reload or the next
 * page of the site carries the conversation on instead of opening another:
 * `usereq:session:<widgetId>` in `localStorage` - per origin by the
 * browser's own rule, which is also the origin the token is bound to, so
 * a stored token can never be presented from anywhere but the site that
 * minted it. `localStorage` rather than `sessionStorage` on purpose: a
 * visitor who closes the tab and comes back within the day expects the
 * same thread, and two tabs of the site are one visitor, not two. The
 * token's own expiry is the only clock; past it the entry is forgotten and
 * the next send opens afresh.
 */

export interface StoredSession {
  token: string
  conversationId: string
  /** ISO timestamp - the token's `expiresAt`. */
  expiresAt: string
}

const key = (widgetId: string) => `usereq:session:${widgetId}`

export function loadSession(
  widgetId: string,
  now: number = Date.now()
): StoredSession | null {
  const raw = read("localStorage", key(widgetId))
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !("token" in parsed) ||
      typeof parsed.token !== "string" ||
      !("conversationId" in parsed) ||
      typeof parsed.conversationId !== "string" ||
      !("expiresAt" in parsed) ||
      typeof parsed.expiresAt !== "string"
    ) {
      return null
    }
    const expires = Date.parse(parsed.expiresAt)
    if (!Number.isFinite(expires) || expires <= now) {
      remove("localStorage", key(widgetId))
      return null
    }
    return {
      token: parsed.token,
      conversationId: parsed.conversationId,
      expiresAt: parsed.expiresAt,
    }
  } catch {
    // Not what we wrote: forgotten, the same as never stored.
    return null
  }
}

export function saveSession(widgetId: string, session: StoredSession) {
  write("localStorage", key(widgetId), JSON.stringify(session))
}

export function clearSession(widgetId: string) {
  remove("localStorage", key(widgetId))
}
